import * as React from 'react'
import { cn } from '@/lib/utils'
import { Label } from '@/design-system/atoms/Label'

export interface FormFieldProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'id'
> {
  label?: React.ReactNode
  htmlFor?: string
  required?: boolean
  hint?: React.ReactNode
  error?: React.ReactNode
  children: React.ReactNode
  orientation?: 'vertical' | 'horizontal'
}

/**
 * Agrupa Label + controle + mensagem de ajuda/erro.
 * Passe um `children` (Input/Textarea/etc) com `id` igual ao `htmlFor`.
 */
export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      className,
      label,
      htmlFor,
      required,
      hint,
      error,
      children,
      orientation = 'vertical',
      ...rest
    },
    ref
  ) => {
    const hintId = hint ? `${htmlFor ?? 'field'}-hint` : undefined
    const errorId = error ? `${htmlFor ?? 'field'}-error` : undefined
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

    return (
      <div
        ref={ref}
        className={cn(
          orientation === 'horizontal'
            ? 'grid grid-cols-[minmax(0,1fr)_2fr] items-start gap-3'
            : 'flex flex-col gap-1.5',
          className
        )}
        {...rest}
      >
        {label && (
          <Label
            htmlFor={htmlFor}
            required={required}
            variant={error ? 'error' : undefined}
          >
            {label}
          </Label>
        )}
        <div className="flex flex-col gap-1">
          {React.isValidElement<Record<string, unknown>>(children)
            ? React.cloneElement(children, {
                id: htmlFor,
                'aria-invalid': !!error || undefined,
                'aria-describedby': describedBy,
                ...(error ? { variant: 'error' } : {}),
              })
            : children}
          {error ? (
            <p
              id={errorId}
              role="alert"
              className="text-xs font-medium text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          ) : hint ? (
            <p id={hintId} className="text-xs text-gray-500 dark:text-gray-400">
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    )
  }
)

FormField.displayName = 'FormField'
